import { Button, Icon } from "pepsico-ds";
import { useState } from "react";
import {
  currencyStep,
  formatMinorCurrency,
  majorToMinor,
  minorToMajorInput,
} from "./tierThresholdMoney";

// ----- Tier Card (main page view) -----
function TierCard({ tier, index, currencyCode, currencyExponent }) {
  return (
    <div className="tier-card">
      <div className="tier-card-header">
        <span className="tier-level-label">
          {index + 1} {index === 0 ? "- Base Tier" : ""}
        </span>
        <span className="tier-multiplier-tag">x{tier.multiplier} multiplier</span>
      </div>
      <div className="tier-card-name">{tier.name}</div>
      <div className="tier-card-detail">
        Entry threshold:{" "}
        <strong>
          {formatMinorCurrency(
            tier.entryThresholdMinor,
            currencyCode,
            currencyExponent
          )}
        </strong>
      </div>
    </div>
  );
}

// ----- Edit Tiers Modal -----
function EditTiersModal({
  tiers,
  serverTiers,
  isActive,
  currencyCode,
  currencyExponent,
  onClose,
  onApply,
  saveTiers,
}) {
  const [editTiers, setEditTiers] = useState(() =>
    (tiers || []).map((tier) => ({
      ...tier,
      entryThresholdMajor: minorToMajorInput(
        tier.entryThresholdMinor,
        currencyExponent
      ),
    }))
  );
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState("");
  const tiersInMinorUnits = editTiers.map((tier) => ({
    ...tier,
    entryThresholdMinor: majorToMinor(tier.entryThresholdMajor, currencyExponent),
  }));

  const handleChange = (id, field, value) => {
    setEditTiers((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
    setValidationError("");
  };

  const handleAddTier = () => {
    setEditTiers((prev) => [
      ...prev,
      {
        id: `tier-new-${Date.now()}`,
        name: "",
        entryThresholdMajor: "0",
        multiplier: 1,
        benefits: [],
      },
    ]);
  };

  const handleDeleteTier = (id) => {
    setEditTiers((prev) => prev.filter((t) => t.id !== id));
    setValidationError("");
  };

  const handleApply = async () => {
    // Validate: all tiers must have a name
    const emptyNameTier = editTiers.find((t) => !t.name || !t.name.trim());
    if (emptyNameTier) {
      setValidationError(
        "All tiers must have a name. Please provide a name for each tier."
      );
      return;
    }

    if (tiersInMinorUnits.some((tier) => tier.entryThresholdMinor === null)) {
      setValidationError(
        `Entry thresholds must use no more than ${currencyExponent || 0} decimal places.`
      );
      return;
    }

    // Validate: no duplicate thresholds after conversion to the backend's minor units.
    const thresholds = tiersInMinorUnits.map((tier) => tier.entryThresholdMinor);
    const uniqueThresholds = new Set(thresholds);
    if (uniqueThresholds.size !== thresholds.length) {
      setValidationError("Entry threshold values must be unique across all tiers.");
      return;
    }

    // Sort tiers by entryThresholdMinor so the lowest is the base tier
    const sortedTiers = [...tiersInMinorUnits].sort(
      (a, b) => Number(a.entryThresholdMinor) - Number(b.entryThresholdMinor)
    );

    setIsSaving(true);
    try {
      // Find tier IDs that were removed (exist on server but not in editTiers)
      const currentServerTiers = serverTiers || [];
      const deletedTierIds = currentServerTiers
        .filter((st) => !sortedTiers.find((et) => et.id === st.id))
        .map((t) => t.id);

      // Single batched call: deletes + creates + updates (only changed ones)
      await saveTiers(sortedTiers, deletedTierIds);

      onApply();
    } catch (err) {
      // onApply receives the error so the parent can show toast
      onApply(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="edit-tiers-modal-overlay" onClick={onClose}>
      <div className="edit-tiers-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-top-bar">
          <h3>Edit tiers</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </div>
        <div className="modal-body">
          <div className="tier-setup-card">
            <div className="tier-setup-header">
              <h4>Tier Setup</h4>
              {!isActive && (
                <Button size="small" variant="secondary" onClick={handleAddTier}>
                  Add tier
                </Button>
              )}
            </div>

            {isActive && (
              <div className="info-banner">
                <span className="info-icon">i</span>
                <span>
                  This tier structure is active you&apos;re only allowed to edit tier
                  names and multipliers, in order to edit thresholds inactivate it
                  first in the main page
                </span>
              </div>
            )}

            {validationError && (
              <div className="validation-error-banner">{validationError}</div>
            )}

            <table className="edit-tiers-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Tier Name</th>
                  <th>Entry Threshold{currencyCode ? ` – ${currencyCode}` : ""}</th>
                  <th>Tier Multiplier</th>
                  {!isActive && <th></th>}
                </tr>
              </thead>
              <tbody>
                {editTiers.map((tier, idx) => {
                  const minThreshold = Math.min(
                    ...tiersInMinorUnits.map((t) => Number(t.entryThresholdMinor))
                  );
                  const isBaseTier =
                    Number(tiersInMinorUnits[idx].entryThresholdMinor) ===
                    minThreshold;
                  const isDuplicate =
                    tiersInMinorUnits.filter(
                      (candidate) =>
                        candidate.entryThresholdMinor ===
                        tiersInMinorUnits[idx].entryThresholdMinor
                    ).length > 1;

                  return (
                    <tr key={tier.id}>
                      <td>
                        <div className="level-badge">
                          <span className="level-number">{idx + 1}</span>
                          {isBaseTier && (
                            <span className="base-tier-label">- Base tier</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={tier.name}
                          onChange={(e) =>
                            handleChange(tier.id, "name", e.target.value)
                          }
                          placeholder="Tier name"
                          className={
                            !tier.name || !tier.name.trim() ? "input-error" : ""
                          }
                        />
                        {(!tier.name || !tier.name.trim()) && (
                          <span className="field-error-text">
                            Tier name is required
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          step={currencyStep(currencyExponent)}
                          min="0"
                          className={isDuplicate ? "input-error" : ""}
                          value={tier.entryThresholdMajor}
                          onKeyDown={(e) => {
                            if (e.key === "e" || e.key === "E") {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) =>
                            handleChange(
                              tier.id,
                              "entryThresholdMajor",
                              e.target.value
                            )
                          }
                          disabled={isActive}
                          readOnly={isActive}
                        />
                        {isDuplicate && (
                          <span className="field-error-text">
                            Duplicate threshold
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          value={tier.multiplier}
                          onChange={(e) =>
                            handleChange(
                              tier.id,
                              "multiplier",
                              Number(e.target.value)
                            )
                          }
                          step="0.01"
                          min="0.01"
                        />
                      </td>
                      {!isActive && (
                        <td>
                          {idx !== 0 && (
                            <button
                              className="delete-row-btn"
                              onClick={() => handleDeleteTier(tier.id)}
                              title="Delete tier"
                            >
                              <Icon icon="delete" size="small" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          <Button size="medium" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={handleApply}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Apply"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function TierQualificationLevels({
  tiers,
  currencyCode,
  currencyExponent,
}) {
  return (
    <div className="tier-cards-row">
      {(tiers || []).length === 0 && (
        <div className="tier-empty-state">
          No tiers configured yet. Click &quot;Edit tiers&quot; to add tiers.
        </div>
      )}
      {(tiers || []).map((tier, idx) => (
        <TierCard
          key={tier.id}
          tier={tier}
          index={idx}
          currencyCode={currencyCode}
          currencyExponent={currencyExponent}
        />
      ))}
    </div>
  );
}

// Attach EditModal as a static property so the parent can render it
TierQualificationLevels.EditModal = EditTiersModal;
